import { FileDetails } from "@/types/types";
import { UploadFileToS3 } from "@/utils/uploadFilestoS3";
import { useEffect, useState } from "react";
import { FileWithPath, useDropzone } from "react-dropzone";
import { generateFilePathForS3 } from "@/utils/generateFilePathForS3";
import { Upload, X, FileText, Loader2, ExternalLink } from "lucide-react";

interface MinimalFileUploadProps {
  videoId?: number;
  onFilesUploaded: (files: FileDetails[]) => void;
  maxFiles?: number;
  defaultFiles?: FileDetails[];
  maxSize?: number; // in bytes
  accept?: { [key: string]: string[] };
  clearFiles?: boolean;
  identifier: string;
  key: string;
  onClear?: () => void;
}

const MinimalFileUpload = ({
  onFilesUploaded,
  maxFiles = 10,
  defaultFiles,
  maxSize = 100 * 1024 * 1024, // 100MB default
  accept = {
    "image/jpeg": [],
    "application/pdf": [],
    "video/mp4": [],
    "image/gif": [],
  },
  clearFiles = false,
  identifier,
  key,
  onClear,
}: MinimalFileUploadProps) => {
  const [localFiles, setLocalFiles] = useState<FileDetails[]>(
    defaultFiles || []
  );
  const [showProgress, setShowProgress] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  // Handle clearing files
  useEffect(() => {
    if (clearFiles) {
      setLocalFiles([]);
      if (onClear) onClear();
    }
  }, [clearFiles, onClear]);

  // Handle file drop
  const onDrop = async (acceptedFiles: FileWithPath[]) => {
    if (uploading) return;
    setUploading(true);

    const newFiles = acceptedFiles.slice(0, maxFiles - localFiles.length);
    const updatedFiles = [...localFiles];
    const uniqueNumber = new Date().getTime();

    for (const file of newFiles) {
      const fileDetails: FileDetails = {
        name: `${file.name}-${uniqueNumber.toString()}`,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        webkitRelativePath: file.webkitRelativePath,
        local_url: URL.createObjectURL(file),
        live_url: null,
      };

      setLocalFiles((prevFiles) => [...prevFiles, fileDetails]);

      try {
        setShowProgress(fileDetails.name);
        const filePath = generateFilePathForS3({
          identifier: identifier as string,
          fileName: file.name,
          context: "analysis-videos",
        });

        const uploadedFile = await UploadFileToS3({
          file: fileDetails,
          filePath: filePath,
        });
        const updatedFile = {
          ...fileDetails,
          live_url: uploadedFile.live_url,
          uploading: false,
        };
        updatedFiles.push(updatedFile);

        setLocalFiles((prevFiles) =>
          prevFiles.map((f) => (f.name === fileDetails.name ? updatedFile : f))
        );

        setShowProgress(null);
      } catch (error) {
        setShowProgress(null);
        console.error(`Failed to upload file: ${file.name}`, error);
      }
    }

    setUploading(false);
    onFilesUploaded(updatedFiles);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = localFiles.filter((_, i) => i !== index);
    setLocalFiles(updatedFiles);
    onFilesUploaded(updatedFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    maxSize,
    accept,
  });

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Get accepted file types string
  const getAcceptedTypes = (): string => {
    const types = Object.keys(accept).map((key) => {
      if (key.includes("image")) return "Images";
      if (key.includes("video")) return "Videos";
      if (key.includes("pdf")) return "PDFs";
      return key.split("/")[1]?.toUpperCase();
    });
    return [...new Set(types)].join(", ");
  };

  return (
    <div className="space-y-3">
      {/* Upload Area */}
      {localFiles?.length < maxFiles && (
        <div
          {...getRootProps()}
          className={`
            relative border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all
            ${
              isDragActive
                ? "border-blue-500 bg-blue-50"
                : "border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50"
            }
          `}
        >
          <input {...getInputProps()} className="absolute inset-0 opacity-0" />
          
          <div className="flex flex-col items-center gap-3">
            <div className={`
              w-12 h-12 rounded-full flex items-center justify-center transition-colors
              ${isDragActive ? "bg-blue-100" : "bg-gray-100"}
            `}>
              <Upload className={`w-6 h-6 ${isDragActive ? "text-blue-600" : "text-gray-400"}`} />
            </div>
            
            <div>
              <p className="text-sm text-gray-700">
                <span className="font-medium text-blue-600">Click to upload</span>
                {" "}or drag and drop
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {getAcceptedTypes()} (max {Math.round(maxSize / (1024 * 1024))}MB)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Uploaded Files List */}
      {localFiles.length > 0 && (
        <div className="space-y-2">
          {localFiles.map((file, index) => (
            <div
              key={index}
              className="flex items-start gap-3 p-3 bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
            >
              {/* File Icon */}
              <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                {file.type.startsWith("image") ? (
                  <img
                    src={file.local_url}
                    alt={file.name}
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : file.type.startsWith("video") ? (
                  <video
                    src={file.local_url}
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <FileText className="w-5 h-5 text-gray-400" />
                )}
              </div>

              {/* File Details */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {file.name.replace(/-\d+$/, "")}
                </p>
                <p className="text-xs text-gray-500 mt-0.5">
                  {formatFileSize(file.size)}
                </p>

                {/* Progress Bar */}
                {showProgress === file.name && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 mb-1">
                      <Loader2 className="w-3 h-3 text-blue-600 animate-spin" />
                      <span className="text-xs text-blue-600 font-medium">Uploading...</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1 overflow-hidden">
                      <div className="bg-blue-600 h-full animate-pulse" style={{ width: "60%" }}></div>
                    </div>
                  </div>
                )}

                {/* View Link */}
                {file.live_url && !showProgress && (
                  <a
                    href={file.live_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-1.5 font-medium"
                  >
                    <ExternalLink className="w-3 h-3" />
                    View file
                  </a>
                )}
              </div>

              {/* Remove Button */}
              <button
                type="button"
                onClick={() => handleRemoveFile(index)}
                className="flex-shrink-0 p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                disabled={showProgress === file.name}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Max Files Warning */}
      {localFiles.length >= maxFiles && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
          <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-amber-600 text-xs font-bold">!</span>
          </div>
          <p className="text-xs text-amber-700">
            Maximum number of files ({maxFiles}) reached. Remove a file to upload more.
          </p>
        </div>
      )}
    </div>
  );
};

export default MinimalFileUpload;